import React, { useEffect, useRef } from "react";
import "./Particles.css";

export default function Particles() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let animationId;
    let canvasWidth = window.innerWidth;
    let canvasHeight = window.innerHeight;

    const particles = [];
    const particleDensity = 0.000032;
    const minParticles = 30;
    const maxParticles = 90;
    const pointer = { x: null, y: null, radius: 140 };
    const pointerStrength = 1.15;

    const calculateParticleCount = () => {
      return Math.max(
        minParticles,
        Math.min(maxParticles, Math.floor(canvasWidth * canvasHeight * particleDensity))
      );
    };

    const resizeCanvas = () => {
      canvasWidth = window.innerWidth;
      canvasHeight = window.innerHeight;
      const dpr = window.devicePixelRatio || 1;

      canvas.style.width = `${canvasWidth}px`;
      canvas.style.height = `${canvasHeight}px`;
      canvas.width = canvasWidth * dpr;
      canvas.height = canvasHeight * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      updateParticleCount();
    };

    class Particle {
      constructor() {
        this.x = Math.random() * canvasWidth;
        this.y = Math.random() * canvasHeight;
        this.size = Math.random() * 2 + 0.6;
        const speedBase = 0.25 + Math.min(0.35, canvasWidth / 2200);
        this.speedX = Math.random() * speedBase - speedBase / 2;
        this.speedY = Math.random() * speedBase - speedBase / 2;
        this.opacity = Math.random() * 0.5 + 0.25;
      }

      update() {
        this.x += this.speedX;
        this.y += this.speedY;

        if (pointer.x !== null) {
          const dx = this.x - pointer.x;
          const dy = this.y - pointer.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < pointer.radius && distance > 0) {
            const force = ((pointer.radius - distance) / pointer.radius) * pointerStrength;
            const directionX = dx / distance;
            const directionY = dy / distance;

            this.x += directionX * force * 1.2;
            this.y += directionY * force * 1.2;

            this.speedX += directionX * force * 0.004;
            this.speedY += directionY * force * 0.004;
          }
        }

        if (this.x > canvasWidth) this.x = 0;
        if (this.x < 0) this.x = canvasWidth;
        if (this.y > canvasHeight) this.y = 0;
        if (this.y < 0) this.y = canvasHeight;
      }

      draw() {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(123, 47, 255, ${this.opacity})`;
        ctx.fill();
      }
    }

    const init = () => {
      particles.length = 0;
      const count = calculateParticleCount();
      for (let i = 0; i < count; i++) {
        particles.push(new Particle());
      }
    };

    const updateParticleCount = () => {
      const targetCount = calculateParticleCount();
      if (particles.length < targetCount) {
        for (let i = particles.length; i < targetCount; i++) {
          particles.push(new Particle());
        }
      } else if (particles.length > targetCount) {
        particles.splice(targetCount);
      }
    };

    const handlePointerMove = (event) => {
      const point = event.touches ? event.touches[0] : event;
      pointer.x = point.clientX;
      pointer.y = point.clientY;
    };

    const handlePointerLeave = () => {
      pointer.x = null;
      pointer.y = null;
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvasWidth, canvasHeight);

      particles.forEach(particle => {
        particle.update();
        particle.draw();
      });

      animationId = requestAnimationFrame(animate);
    };

    resizeCanvas();
    init();
    animate();

    window.addEventListener("resize", resizeCanvas);
    window.addEventListener("mousemove", handlePointerMove);
    window.addEventListener("touchmove", handlePointerMove, { passive: true });
    window.addEventListener("mouseleave", handlePointerLeave);
    window.addEventListener("mouseout", handlePointerLeave);

    return () => {
      window.removeEventListener("resize", resizeCanvas);
      window.removeEventListener("mousemove", handlePointerMove);
      window.removeEventListener("touchmove", handlePointerMove);
      window.removeEventListener("mouseleave", handlePointerLeave);
      window.removeEventListener("mouseout", handlePointerLeave);
      cancelAnimationFrame(animationId);
    };
  }, []);

  return <canvas ref={canvasRef} className="particles-canvas" />;
}